import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6" data-testid={testid || "page-header"}>
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border bg-card p-4 flex flex-col gap-2 transition-colors hover:border-primary/40">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
        {Icon && <Icon className={cn("h-4 w-4", accent ? "text-red-600" : "text-muted-foreground")} />}
      </div>
      <div className="font-mono tabular-nums text-xl sm:text-2xl font-semibold leading-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

const STATUS = {
  full: "success", active: "success", posted: "success", approved: "info", partial: "warning", submitted: "warning",
  uploaded: "warning", posting: "info", empty: "neutral", inactive: "neutral", cancelled: "neutral", reversed: "danger",
  rejected: "danger", reversing: "info", draft: "neutral", pending: "warning",
};
const TONE = {
  success: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  warning: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  info: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
  neutral: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
  danger: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
};
export function StatusBadge({ status, testid }) {
  return (
    <Badge variant="outline" data-testid={testid} className={cn("capitalize font-medium", TONE[STATUS[status] || "neutral"])}>
      {status}
    </Badge>
  );
}

export function Empty({ text, testid }) {
  return <div data-testid={testid || "empty-state"} className="py-12 text-center text-sm text-muted-foreground">{text}</div>;
}

export function Field({ label, children, className, required }) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
        {label}{required && <span className="text-red-600"> *</span>}
      </span>
      {children}
    </label>
  );
}

export function Combobox({ options, value, onChange, placeholder = "Select…", testid, className }) {
  const [open, setOpen] = useState(false);
  const sel = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className={cn("flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-left", className)}>
          <span className={cn("truncate", !sel && "text-muted-foreground")}>{sel ? sel.label : placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
        <Command>
          <CommandInput placeholder="Search…" data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.value} value={`${o.label} ${o.value}`} data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  onSelect={() => { onChange(o.value); setOpen(false); }}>
                  <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const tableWrap = "rounded-lg border bg-card overflow-x-auto";
export const th = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap bg-muted/50";
export const td = "px-3 py-2 text-sm whitespace-nowrap border-t";
export const num = "font-mono tabular-nums text-right";
